import { API_URL } from "../../../config";

const DeviceGroupId = {
  WeightHeight: 1,
  Body: 2,
  Thermometer: 3,
  BP: 4,
  SPO2: 5,
  ECG: 6,
  Stethoscope: 7,
  Otoscope: 10,
  NGSP: 11,
  CH: 12,
  BG: 13,
  Thermal: null,
  BS: null,
};
const DeviceId = {
  Weight: 2,
  BMI: 3,
  BPSys: 18,
  BPDia: 19,
  SPO2: 21,
  SPO2Hr: 22,
  BPCombined: "BPCombined",
  Temperature: 17,
  Height: 1,
  FatPercentage: 4,
};

const getDeviceNameFromId = (id) => {
  if (id === DeviceId.BPCombined) return "BP: Sys/Dia";
  if (id === DeviceId.SPO2Hr) return "Heart Rate";
  if(id === DeviceId.FatPercentage) return "Body Composition";
  return Object.keys(DeviceId).find((key) => DeviceId[key] === id);
};

const DeviceIds = {
  Height: 1,
  Weight: 2,
  BMI: 3,
  FatPercentage: 4,
  FatMass: 5,
  MuscleRate: 6,
  WaterRate: 7,
  Moisture: 8,
  ExtraCellularFluid: 9,
  IntraCellularFluid: 10,
  BasalMetabolism: 11,
  VisceralFat: 12,
  Protein: 13,
  Mineral: 14,
  BodyAge: 15,
  Score: 16,
  Temperature: 17,
  BPSys: 18,
  BPDia: 19,
  BPHr: 20,
  SPO2: 21,
  SPO2Hr: 22,
  ECGGraph: 23,
  ECGHr: 24,
  ECGRr: 25,
  ECGP: 26,
  ECGPr: 27,
  ECGQrs: 28,
  ECGQt: 29,
  ECGQtc: 30,
  ECGPAmp: 31,
  ECGQPAmp: 32,
  ECGQTAmp: 33,
  ECGQConclusion: 34,
  StehHeart: 35,
  StehLungs: 36,
  StehBowel: 37,
  Otoscope: 38,
  NGSP: 39,
  Triglycerides: 45,
  Cholesterol: 42,
  HDL: 43,
  LDL: 44,
  BG: 46,
};

const getDeviceNameFromIds = (id) => {
  return Object.keys(DeviceIds).find((key) => DeviceIds[key] === id);
};

const fetchRequest = async (url, options) => {
  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Fetch request failed", error);
    throw error;
  }
};

const hasKey = (obj, key) => {
  return obj && Object.prototype.hasOwnProperty.call(obj, key);
};

const isArrayOf = (array, checkFn) => {
  return Array.isArray(array) && array.every(checkFn);
};

const isDevice = (unknown) => {
  if (
    hasKey(unknown, "id") &&
    hasKey(unknown, "code") &&
    hasKey(unknown, "name") &&
    hasKey(unknown, "units") &&
    hasKey(unknown, "device_group") &&
    hasKey(unknown, "device_type")
  ) {
    return true;
  }
  return false;
};

const isArrayOfDevices = (unknown) => {
  return isArrayOf(unknown, isDevice);
};

const ServiceApi = {
  get: (url, params = {}, isValid = () => true) => {
    return fetchRequest(url, { method: "GET" }).then((response) => {
      if (!isValid(response)) throw new Error("Invalid response");
      return response;
    });
  },
  post: (url, params = {}, isValid = () => true) => {
    return fetchRequest(url, { method: "POST", body: params.body }).then(
      (response) => {
        if (!isValid(response)) throw new Error("Invalid response");
        return response;
      },
    );
  },
  patch: (url, params = {}, isValid = () => true) => {
    return fetchRequest(url, { method: "PATCH", body: params.body }).then(
      (response) => {
        if (!isValid(response)) throw new Error("Invalid response");
        return response;
      },
    );
  },
};
const DeviceApi = {
  getDevices: () => {
    return ServiceApi.get(`${API_URL}/device/`, {}, isArrayOfDevices);
  },
  addDevice: (device) => {
    return ServiceApi.post(
      `${API_URL}/device/`,
      { body: JSON.stringify(device) },
      isDevice,
    );
  },
  updateDevice: (device_id, device) => {
    return ServiceApi.patch(
      `${API_URL}/device/${device_id}`,
      { body: JSON.stringify(device) },
      isDevice,
    );
  },
  toggleDevice: ({ doctorId, appointmentId, event, deviceGroup }) => {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    return fetchRequest(`${API_URL}/appointment/doctor_event/`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        doctor_id: doctorId,
        appointment_id: appointmentId,
        event,
        device_group: deviceGroup,
      }),
    });
  },
  getDeviceSummary: ({ appointmentId, deviceGroup }) => {
    const queryParams = new URLSearchParams();
    if (appointmentId) queryParams.append("appointment_id", appointmentId);
    if (deviceGroup) queryParams.append("device_group", deviceGroup.toString());

    const url = `${API_URL}/device/appointment_summary/?${queryParams.toString()}`;

    return fetchRequest(url, {
      method: "GET",
    });
  },
  getDeviceReadings: (params) => {
    const queryParams = new URLSearchParams(params);
    return fetchRequest(
      `${API_URL}/device/readings/?${queryParams.toString()}`,
      {
        method: "GET",
      },
    );
  },
  getEcgReadings: (params) => {
    const queryParams = new URLSearchParams(params);
    return fetchRequest(
      `${API_URL}/device/ecg_summary?${queryParams.toString()}`,
      { method: "GET" },
    );
  },
};
const statusVideoCall = {
  Upcoming: "UPCOMING",
  Completed: "COMPLETED",
  DispenseMedicine: "DISPENSE_MEDICINE",
  Cancelled: "CANCELLED",
  InProgress: "IN_PROGRESS",
  Missed: "MISSED",
  Unknown: "UNKNOWN",
};
const actionTypes = {
  Join: "JOIN",
  Device: "DEVICE",
  Summary: "SUMMARY",
  Stop: "END",
  StopDevice: "STOP_DEVICE",
};

const getInitials = (name) => {
  return name ? name.charAt(0) : "";
};

export {
  DeviceGroupId,
  DeviceId,
  DeviceIds,
  getDeviceNameFromId,
  fetchRequest,
  hasKey,
  isArrayOf,
  isDevice,
  isArrayOfDevices,
  DeviceApi,
  statusVideoCall,
  actionTypes,
  getInitials,
  getDeviceNameFromIds,
};
